import React, {
  createContext,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react';
import useAnalyticsTracker from '../../analytics/hooks/useAnalyticsTracker';
import { GlobalSearchTracking } from '../constants/global-search-tracking';
import { useKeyboardNavigation } from '../../a11y/scroll/hooks/useKeyboardNavigation';
import {
  GlobalSearchEntityFilter,
  GlobalSearchState,
  IGlobalSearchRegistry
} from '../interfaces/global-search';
import { GlobalSearchReducer } from './reducer/globalSearchReducer';
import { GlobalSearchReducerActions, GlobalSearchReducerType } from './reducer/interfaces';
import { GlobalSearchReducerActionsFactory } from './reducer/actions';
import { getGlobalSearchInitialState, getGlobalSearchUIRenderingData } from './utils/globalSearchUtil';


export interface GlobalSearchContextState {
  showBody: boolean;
  showEntities: boolean;
  showNoResultFound: boolean;
  closeDialog: Function;
  onSearch: (searchTerm: string) => void;
  applyFilter: (filters: string[]) => void;
  filterChips: GlobalSearchEntityFilter[];
  searchAreaRef: RefObject<HTMLInputElement>;
  bodyRef: RefObject<HTMLDivElement>;
  state: GlobalSearchState;
}

const INITIAL_STATE: GlobalSearchContextState = {
  showBody: false,
  showEntities: false,
  showNoResultFound: false,
  filterChips: [],
  closeDialog: () => {},
  onSearch: () => {},
  applyFilter: () => {},
  searchAreaRef: { current: null },
  bodyRef: { current: null },
  state: {
    items: {},
    filters: { search: '', entity: []}
  },
};

export const GlobalSearchContext = createContext<GlobalSearchContextState>(INITIAL_STATE);


export function GlobalSearchContextProvider({
  children,
  registry,
  onClose
}: PropsWithChildren<{
  registry: IGlobalSearchRegistry
  onClose: Function;
}>) {
  const {
    initialState,
    filterChips,
    filterSensitiveCBs,
    filterInsensitiveCBs
  } = useMemo(() => getGlobalSearchInitialState(registry),
    [registry]
  );

  const searchAreaRef = useRef<HTMLInputElement>(null);

  const [state, dispatch]: [GlobalSearchState, Function] = useReducer<GlobalSearchReducerType>(
    GlobalSearchReducer,
    { items: initialState, filters: { search: '', entity: [] } }
  );
  const reducerActions = useRef<GlobalSearchReducerActions>(
    GlobalSearchReducerActionsFactory(dispatch)
  );

  const bodyRef = useRef<HTMLDivElement>(null);

  const analyticsTracker = useAnalyticsTracker();

  useEffect(() => {
    trackEvent(GlobalSearchTracking.TRACKER_GLOBAL_SEARCH_OPEN, {});

    [...filterInsensitiveCBs, ...filterSensitiveCBs].forEach(({cb, entity}) => {
      reducerActions.current.getEntityData(entity);
      cb(state.filters).then((res) => {
        reducerActions.current.updateEntityData(entity, { entities: res });
      });
    });

    return () => {
      trackEvent(GlobalSearchTracking.TRACKER_GLOBAL_SEARCH_CLOSE, {});
    };
  }, [
    filterInsensitiveCBs,
    filterSensitiveCBs
  ]);

  const trackEvent = (eventName: string, properties: object) => {
    analyticsTracker.eventTrack({
      action: eventName,
      properties: { ...properties }
    });
  };

  const {
    loadingData,
    ...rest
  } = useMemo(() => getGlobalSearchUIRenderingData(state), [state]);

  useKeyboardNavigation({
    listRef: bodyRef,
    listFetchComplete: !loadingData
  });

  const onSearch = useCallback((value: string) => {
    reducerActions.current.filterData({ search: value || '' });

    filterSensitiveCBs.forEach(({cb, entity}) => {
      reducerActions.current.getEntityData(entity);

      cb({
        search: value,
        entity: state.filters.entity
      }).then((res) => {
        reducerActions.current.updateEntityData(entity, { entities: res });
      });
    });

  }, [state.filters.entity]);

  const applyFilter = useCallback((_filters: string[]) => {
    reducerActions.current.filterData({ entity: _filters });
  }, []);

  const closeDialog = useCallback(() => {
    onClose();
  }, [onClose]);

  const providerValues: GlobalSearchContextState = useMemo(() => ({
    ...rest,
    closeDialog,
    applyFilter,
    onSearch,
    state,
    searchAreaRef,
    bodyRef,
    filterChips
  }), [
    rest,
    closeDialog,
    applyFilter,
    onSearch,
    state,
    searchAreaRef,
    bodyRef,
    filterChips
  ]);

  return (
    <GlobalSearchContext.Provider value={providerValues}>
      {children}
    </GlobalSearchContext.Provider>
  );
}
